/**
 * AppreciationSection — Photo-centric member shoutout cards. v2
 * Layout: Section title → grid of cards (columnsPerRow: 1/2/3)
 * Each card: circular photo(s) (top-center) → bold name → rich-text description.
 * A card can hold up to MAX_MEMBER_PHOTOS photos, drawn as an overlapping row
 * so group shoutouts read as one unit. No stars. Card background is per-member.
 */
import React from 'react';
import { resolveAssetUrl } from '../lib/assetUrl';
import { memberPhotos } from '../lib/memberPhotos';

// Circles shrink as the row grows so five of them still fit a 3-column card.
const avatarSize = (count) => (count <= 1 ? 72 : count <= 3 ? 60 : 52);

export function AppreciationSection({ section, isSelected, updateSection }) {
  const data    = section.data     || {};
  const st      = section.settings || {};

  const title   = data.title   ?? 'Appreciation';
  const members = data.members ?? [];
  const columns = st.columnsPerRow ?? 2;

  const bg      = st.backgroundColor || '#ffffff';
  const textCol = st.textColor       || '#111827';
  const padding = st.padding         || '28px 24px';

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const gridCols =
    columns === 1 ? '1fr' :
    columns === 3 ? '1fr 1fr 1fr' :
                    '1fr 1fr';

  return (
    <div style={{ backgroundColor: bg, padding }}>

      {/* ── Section title ─────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        {isSelected ? (
          <input
            value={title}
            onChange={(e) => updateData({ title: e.target.value })}
            placeholder="Section title"
            style={{
              border: 'none', borderBottom: '1px dashed #d1d5db',
              fontSize: 22, fontWeight: 700, color: textCol,
              background: 'transparent', outline: 'none', minWidth: 180,
              fontFamily: "'Instrument Serif', serif",
            }}
          />
        ) : (
          <div style={{ fontSize: 22, fontWeight: 700, color: textCol, fontFamily: "'Instrument Serif', serif" }}>{title}</div>
        )}
      </div>

      {/* ── Member cards grid ─────────────────────────────────── */}
      {members.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          gap: 16,
        }}>
          {members.map((m) => {
            const photos  = memberPhotos(m);
            const size    = avatarSize(photos.length);
            const overlap = Math.round(size * 0.25);
            return (
            <div
              key={m.id}
              style={{
                background:   m.cardColor || '#e9e0cc',
                borderRadius: 12,
                padding:      '24px 16px',
                textAlign:    'center',
              }}
            >
              {/* Circular photo(s) — overlapping row, centred */}
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                marginBottom: 14,
              }}>
                {photos.length > 0 ? photos.map((url, i) => (
                  <div
                    key={`${m.id}-photo-${i}`}
                    style={{
                      width: size, height: size,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      background: '#c9bfaf',
                      border: photos.length > 1 ? '3px solid #ffffff' : 'none',
                      marginLeft: i === 0 ? 0 : -overlap,
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={resolveAssetUrl(url)}
                      alt={m.name || 'Member'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </div>
                )) : (
                  <div style={{
                    width: size, height: size,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#c9bfaf',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="#888">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Name */}
              <div style={{
                fontSize: 14, fontWeight: 700, color: textCol,
                marginBottom: 8,
              }}>
                {m.name || 'Member Name'}
              </div>

              {/* Description (HTML) */}
              {m.descriptionHtml ? (
                <div
                  style={{ fontSize: 14, color: textCol, lineHeight: 1.7 }}
                  dangerouslySetInnerHTML={{ __html: m.descriptionHtml }}
                />
              ) : (
                <div style={{ fontSize: 14, color: textCol + '88', fontStyle: 'italic' }}>
                  {isSelected ? 'Edit description in settings panel' : 'No description added'}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center', padding: '32px 16px',
          border: '1.5px dashed #d1d5db', borderRadius: 8,
          color: '#9ca3af', fontSize: 14,
        }}>
          {isSelected
            ? 'Add members using the settings panel on the right'
            : 'No members added yet'
          }
        </div>
      )}
    </div>
  );
}
